import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {styled} from '@mui/system';
import * as Sentry from '@sentry/react';
import {Storage} from 'aws-amplify';
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import {useFieldArray, useForm} from 'react-hook-form';
import {useQueryClient} from 'react-query';
import {useNavigate} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import {DeleteRuleButton} from './DeleteRuleButton';
import {MoneyInfo} from './MoneyInfo';
import {MoneyInfoTableHead} from './MoneyInfoTableHead';
import {initialValue, RoomCostInfo} from './RoomCostInfo';
import {RoomEarlyRepaymentInfo} from './RoomEarlyRepaymentInfo';
import {Snackbar} from './Snackbar';
import {CreatePropertyMutationVariables, CustomItemInput} from '../API';
import {useOAuthUser, useOAuthUserState} from '../AuthProvider';
import {AddItemButton} from '../components/AddItemButton';
import {Button} from '../components/Button';
import {Cell} from '../components/Cell';
import {DatePicker} from '../components/DatePicker';
import {IconButton} from '../components/IconButton';
import {LabelTitle} from '../components/LabelTitle';
import {Select} from '../components/Select';
import {MAX_INPUT_LENGTH, TextField} from '../components/TextField';
import {DeleteIcon} from '../images/DeleteIcon';
import {ImageSquareIcon} from '../images/ImageSquareIcon';
import {PlusIcon} from '../images/PlusIcon';
import {useCreatePropertyMutation} from '../queries/property';
import {useCreateResidentMutation} from '../queries/resident';
import {useCreateRoomMutation} from '../queries/room';
import {Input, PropertyFormInput} from '../screens/AddProperty';
import {ResidentFormInput} from '../screens/AddResident';
import {
  RoomBasicInfoType,
  RoomCostInfoType,
  RoomEarlyRepaymentInfoType,
} from '../screens/AddRoom';
import {convertStringToStructureType} from '../utils/convertStringToStructureType';
import {isOversizedImage} from '../utils/isOversizedImage';
import {
  conditionList,
  frequencyList,
  layoutList,
  monthList,
  repaymentMethodList,
  structureList,
  yearsList,
} from '../utils/selectOptions';
import {toISOStringWithTimezone} from '../utils/toISOStringWithTimezone';

export type PropertyRegistrationWithOcrType = Omit<
  PropertyFormInput,
  'years'
> & {
  years: string | null;
} & Omit<RoomBasicInfoType, 'name' | 'customItems'> & {
    roomName: RoomBasicInfoType['name'];
    roomCustomItems: CustomItemInput[];
  } & RoomCostInfoType &
  RoomEarlyRepaymentInfoType &
  Omit<ResidentFormInput, 'name' | 'customItems'> & {
    residentName: ResidentFormInput['name'];
    residentCustomItems: CustomItemInput[];
  };

type Props = {
  isAnalyzing: boolean;
  data: PropertyRegistrationWithOcrType;
};

export const PropertyRegistrationFormWithOcr: FC<Props> = ({
  isAnalyzing,
  data,
}) => {
  const navigate = useNavigate();
  const user = useOAuthUser();
  const userState = useOAuthUserState();
  const userId = userState.user?.attributes.sub ?? '';
  const queryClient = useQueryClient();
  const [propertyImage, setPropertyImage] = useState<File>();
  const [propertyImageURL, setPropertyImageURL] = useState<string>();
  const [years, setYears] = useState<Date | null>(null);
  const [isYearsError, setIsYearsError] = useState<boolean>(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [isOverImageSize, setIsOverImageSize] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);
  const [repaymentDate, setRepaymentDate] = useState<Date | null>(null);
  const [isRoomInfoError, setIsRoomInfoError] = useState(false);
  const [isSaveButtonDown, setIsSaveButtonDown] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const {mutate: createProperty, isLoading: isLoadingCreateProperty} =
    useCreatePropertyMutation();
  const {mutate: createRoom, isLoading: isLoadingCreateRoom} =
    useCreateRoomMutation();
  const {mutate: createResident, isLoading: isLoadingCreateResident} =
    useCreateResidentMutation();

  const {
    register,
    handleSubmit,
    formState: {errors},
    setValue,
    getValues,
    control,
  } = useForm<PropertyRegistrationWithOcrType>({
    mode: 'onBlur',
  });
  const {fields, append, remove} = useFieldArray({
    control,
    name: 'customItems',
  });
  const {
    fields: roomCustomItemsFields,
    append: appendRoomCustomItems,
    remove: removeRoomCustomItems,
  } = useFieldArray({
    control,
    name: 'roomCustomItems',
  });
  const {
    fields: keyMoneyFields,
    append: keyMoneyAppend,
    remove: keyMoneyRemove,
  } = useFieldArray({
    control,
    name: 'keyMoney',
  });
  const {
    fields: firstIncomeFields,
    append: firstIncomeAppend,
    remove: firstIncomeRemove,
  } = useFieldArray({
    control,
    name: 'firstIncome',
  });
  const {
    fields: rentFields,
    append: rentAppend,
    remove: rentRemove,
  } = useFieldArray({
    control,
    name: 'rent',
  });
  const {
    fields: customItemsFields,
    append: customItemsAppend,
    remove: customItemsRemove,
  } = useFieldArray({
    control,
    name: 'residentCustomItems',
  });
  const {
    fields: customMoveInCostItemsFields,
    append: customMoveInCostItemsAppend,
    remove: customMoveInCostItemsRemove,
    update: customMoveInCostItemsUpdate,
  } = useFieldArray({
    control,
    name: 'customMoveInCostItems',
  });
  const {
    fields: advertisingRatesFields,
    append: advertisingRatesAppend,
    remove: advertisingRatesRemove,
  } = useFieldArray({
    control,
    name: 'advertisingRates',
  });
  const {
    fields: customMoveOutCostItemsFields,
    append: customMoveOutCostItemsAppend,
    remove: customMoveOutCostItemsRemove,
    update: customMoveOutCostItemsUpdate,
  } = useFieldArray({
    control,
    name: 'customMoveOutCostItems',
  });
  const isNotValidDeliveryDate = useMemo(
    () => !deliveryDate || isNaN(deliveryDate.getDate()),
    [deliveryDate],
  );
  const isNotValidStartDate = useMemo(
    () => !startDate || isNaN(startDate.getDate()),
    [startDate],
  );
  const isLoading = useMemo(
    () =>
      isLoadingCreateProperty || isLoadingCreateRoom || isLoadingCreateResident,
    [isLoadingCreateProperty, isLoadingCreateResident, isLoadingCreateRoom],
  );
  useEffect(() => {
    if (data) {
      Object.keys(data).forEach(key => {
        if (
          ['years', 'deliveryDate', 'repaymentDate', 'startDate'].includes(key)
        ) {
          switch (key) {
            case 'years':
              setYears(data.years ? new Date(data.years) : null);
              break;
            case 'deliveryDate':
              setDeliveryDate(
                data.deliveryDate ? new Date(data.deliveryDate) : null,
              );
              break;
            case 'repaymentDate':
              setRepaymentDate(
                data.repaymentDate ? new Date(data.repaymentDate) : null,
              );
              break;
            case 'startDate':
              setStartDate(data.startDate ? new Date(data.startDate) : null);
              break;
          }
        }
        setValue(
          key as keyof PropertyRegistrationWithOcrType,
          data[key as keyof PropertyRegistrationWithOcrType],
        );
      });
    }
  }, [data, setValue]);
  const onFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.files == null) {
        return;
      }
      const file = e.currentTarget.files[0];
      isOversizedImage(file)
        ? setIsOverImageSize(true)
        : setIsOverImageSize(false);
      setPropertyImage(file);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setPropertyImageURL(reader.result as string);
      };
      e.target.value = '';
    },
    [],
  );
  const onChangeCostInfo = useCallback(
    (value: RoomCostInfoType) => {
      setValue('purchaseCosts', value.purchaseCosts);
      setValue('propertyTax', value.propertyTax);
      setValue('insuranceFee', value.insuranceFee);
      setValue('realEstateAcquisitionTax', value.realEstateAcquisitionTax);
      setValue('repairReserveFund', value.repairReserveFund);
      setValue('managementFee', value.managementFee);
      setValue('managementCommission', value.managementCommission);
      setValue('customCostItems', value.customCostItems);
    },
    [setValue],
  );
  const onChangeEarlyRepaymentInfo = useCallback(
    (value: RoomEarlyRepaymentInfoType) => {
      setValue('earlyRepaymentInfo', value.earlyRepaymentInfo);
    },
    [setValue],
  );
  const onError = useCallback(() => {
    setOpenErrorSnackbar(true);
  }, []);
  const onSubmit = useCallback(
    async (formData: PropertyRegistrationWithOcrType) => {
      if (!user) {
        navigate('/error');
        return;
      }
      if (isNotValidDeliveryDate || isRoomInfoError || isNotValidStartDate) {
        onError();
        return;
      }
      const propertyId = uuidv4();
      const propertyVariables: CreatePropertyMutationVariables = {
        input: {
          id: propertyId,
          name: formData.name,
          thumbnail: '',
          address: formData.address,
          station: formData.station,
          minutes: formData.minutes,
          structure: convertStringToStructureType(formData.structure),
          stories: formData.stories || null,
          units: formData.units || null,
          years: years ? toISOStringWithTimezone(years) : undefined,
          buildingManagement: formData.buildingManagement,
          rentalManagement: formData.rentalManagement,
          customItems: formData.customItems,
          userPropertiesId: userId,
        },
      };
      try {
        if (propertyImage) {
          await Storage.put(
            `properties/${propertyId}/${encodeURIComponent(
              propertyImage.name,
            )}`,
            propertyImage,
            {
              level: 'private',
            },
          );
          propertyVariables.input.thumbnail = `properties/${propertyId}/${encodeURIComponent(
            propertyImage.name,
          )}`;
        }
        createProperty(propertyVariables, {
          onSuccess: () => {
            const roomId = uuidv4();
            if (!deliveryDate) {
              navigate('/error');
              return;
            }
            createRoom(
              {
                input: {
                  id: roomId,
                  ownerId: user.attributes.sub,
                  name: formData.roomName,
                  layout: formData.layout,
                  condition: formData.condition,
                  deliveryDate: toISOStringWithTimezone(deliveryDate),
                  salesCompany: formData.salesCompany,
                  sellingPrice: formData.sellingPrice,
                  occupiedArea: formData.occupiedArea,
                  totalArea: formData.totalArea,
                  bankName: formData.bankName,
                  loanYears: formData.loanYears,
                  loanAmount: formData.loanAmount,
                  interestRate: formData.interestRate
                    ? formData.interestRate
                    : null,
                  repaymentMethod: formData.repaymentMethod,
                  repaymentDate: repaymentDate
                    ? toISOStringWithTimezone(repaymentDate)
                    : null,
                  customItems: formData.roomCustomItems,
                  purchaseCosts: formData.purchaseCosts,
                  propertyTax: formData.propertyTax,
                  realEstateAcquisitionTax: formData.realEstateAcquisitionTax,
                  insuranceFee: formData.insuranceFee,
                  repairReserveFund: formData.repairReserveFund,
                  managementFee: formData.managementFee,
                  managementCommission: formData.managementCommission,
                  customCostItems: formData.customCostItems,
                  earlyRepaymentInfo: formData.earlyRepaymentInfo,
                  propertyRoomsId: propertyId,
                },
              },
              {
                onSuccess: () => {
                  createResident(
                    {
                      input: {
                        ownerId: user.attributes.sub,
                        name: formData.residentName,
                        startDate: startDate
                          ? toISOStringWithTimezone(startDate)
                          : '',
                        customItems: formData.residentCustomItems,
                        keyMoney: formData.keyMoney,
                        firstIncome: formData.firstIncome,
                        rent: formData.rent,
                        customMoveInCostItems: formData.customMoveInCostItems,
                        roomResidentsId: roomId,
                        advertisingRates: formData.advertisingRates,
                        customMoveOutCostItems: formData.customMoveOutCostItems,
                      },
                    },
                    {
                      onSuccess: () => {
                        queryClient.invalidateQueries();
                        navigate(`/property/${propertyId}`, {
                          state: {operationState: 'add-success-with-ocr'},
                        });
                      },
                      onError: () => {
                        navigate('/error');
                        return;
                      },
                    },
                  );
                },
                onError: () => {
                  navigate('/error');
                  return;
                },
              },
            );
          },
          onError: () => {
            navigate('/error');
            return;
          },
        });
      } catch (err) {
        Sentry.captureException(err);
        navigate('/error');
        return;
      }
    },
    [
      createProperty,
      createResident,
      createRoom,
      deliveryDate,
      isNotValidDeliveryDate,
      isNotValidStartDate,
      isRoomInfoError,
      navigate,
      onError,
      propertyImage,
      queryClient,
      repaymentDate,
      startDate,
      user,
      userId,
      years,
    ],
  );

  return isAnalyzing ? (
    <Stack alignSelf="center" width="100%" alignItems="center">
      <CircularProgress
        sx={{
          width: '60px  !important',
          height: '60px  !important',
        }}
      />
    </Stack>
  ) : (
    <>
      <Stack spacing={4} width="100%" paddingRight={4.5}>
        <Stack spacing={1}>
          <StyledTypography>物件情報</StyledTypography>
          <Grid container spacing={3} width="100%">
            <Grid item padding="0 !important" width="100%">
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle isRequired>物件名称</LabelTitle>
                </Cell>
                <StyledCell>
                  <TextField
                    {...register('name', {required: true})}
                    placeholder="Prop Dock マンション"
                    helperText={
                      errors.name?.type === 'required' &&
                      '物件名を入力してください'
                    }
                    size="small"
                    error={errors.name?.type === 'required'}
                  />
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle isRequired>所在地</LabelTitle>
                </Cell>
                <StyledCell>
                  <TextField
                    {...register('address', {required: true})}
                    placeholder="大阪市北区大深町5-54"
                    helperText={
                      errors.address?.type === 'required' &&
                      '所在地を入力してください'
                    }
                    size="small"
                    error={errors.address?.type === 'required'}
                  />
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle isRequired>最寄駅</LabelTitle>
                </Cell>
                <StyledCell>
                  <TextField
                    {...register('station', {required: true})}
                    placeholder="心斎橋"
                    helperText={
                      errors.station?.type === 'required' &&
                      '最寄り駅を入力してください'
                    }
                    size="small"
                    error={errors.station?.type === 'required'}
                  />
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle isRequired>駅徒歩</LabelTitle>
                </Cell>
                <StyledCell>
                  <TextField
                    type="number"
                    {...register('minutes', {required: true})}
                    placeholder="3"
                    helperText={
                      errors.minutes?.type === 'required' &&
                      '最寄り駅を入力してください'
                    }
                    size="small"
                    error={errors.minutes?.type === 'required'}
                  />
                  <Typography sx={{marginLeft: '4px'}}>分　</Typography>
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle isRequired>構造</LabelTitle>
                </Cell>
                <StyledCell>
                  <FormControl fullWidth>
                    <Select
                      defaultValue={data.structure || ''}
                      displayEmpty
                      {...register('structure', {required: true})}
                      error={errors.structure?.type === 'required'}>
                      <MenuItem value="">選択してください</MenuItem>
                      {structureList.map((s, index) => (
                        <MenuItem value={s.value} key={index}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{color: 'warning.main'}}>
                      {errors.structure?.type === 'required' &&
                        '構造を選択してください'}
                    </FormHelperText>
                  </FormControl>
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle>階建</LabelTitle>
                </Cell>
                <StyledCell>
                  <TextField
                    type="number"
                    {...register('stories', {required: false})}
                    placeholder="6"
                    size="small"
                  />
                  <Typography sx={{marginLeft: '4px', wordBreak: 'keep-all'}}>
                    階建
                  </Typography>
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle>総戸数</LabelTitle>
                </Cell>
                <StyledCell>
                  <TextField
                    type="number"
                    size="small"
                    {...register('units', {required: false})}
                    placeholder="30"
                  />
                  <Typography sx={{marginLeft: '4px'}}>戸　</Typography>
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle>築年月</LabelTitle>
                </Cell>
                <StyledCell>
                  <DatePicker
                    views={['year', 'month']}
                    value={years}
                    inputFormat={'yyyy/MM'}
                    error={isYearsError}
                    onChangeValue={setYears}
                    onErrorChange={setIsYearsError}
                  />
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle>建物管理会社名</LabelTitle>
                </Cell>
                <StyledCell>
                  <TextField
                    size="small"
                    {...register('buildingManagement', {required: false})}
                    placeholder="株式会社 Prop Dock"
                  />
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle>賃貸管理会社名</LabelTitle>
                </Cell>
                <StyledCell>
                  <TextField
                    size="small"
                    {...register('rentalManagement', {required: false})}
                    placeholder="株式会社 Prop Dock"
                  />
                </StyledCell>
              </Stack>
              <Stack direction="row" spacing={0}>
                <Cell color="primary-40">
                  <LabelTitle>サムネイル画像</LabelTitle>
                </Cell>
                <StyledCell>
                  <Stack spacing={1} direction="row">
                    {propertyImageURL && (
                      <StyledImg
                        src={propertyImageURL}
                        alt="Property Thumbnail"
                        loading="lazy"
                      />
                    )}
                    <Stack spacing={1}>
                      {propertyImage ? (
                        <Grid container alignItems="center" spacing={1}>
                          <Grid item maxWidth="250px">
                            <Typography noWrap fontSize="12px">
                              {propertyImage.name}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <IconButton
                              tooltip="削除"
                              sx={{bgcolor: 'flikGray.-25'}}
                              onClick={() => {
                                setPropertyImage(undefined);
                                setPropertyImageURL('');
                                setIsOverImageSize(false);
                              }}>
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ) : null}
                      <label htmlFor="contained-button-file">
                        <Input
                          id="contained-button-file"
                          type="file"
                          accept="image/jpeg, image/png"
                          onChange={onFileChange}
                        />
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ImageSquareIcon color="primary" />}>
                          ファイルを選択
                        </Button>
                      </label>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          lineHeight: '1.3',
                        }}>
                        5MB以内のjpg、png画像をアップロードすることができます
                      </Typography>
                      {isOverImageSize ? (
                        <StyledText>
                          アップロードされているファイルが 5MB を超えています。
                        </StyledText>
                      ) : null}
                    </Stack>
                  </Stack>
                </StyledCell>
              </Stack>
              <Stack direction="column" sx={{width: '100%'}}>
                {fields.map((field, index) => (
                  <Stack key={field.id} direction="row" spacing={0}>
                    <Cell color="primary-40">
                      <TextField
                        {...register(`customItems.${index}.key`, {
                          required: true,
                          maxLength: MAX_INPUT_LENGTH,
                        })}
                        placeholder="項目名"
                        size="small"
                        helperText={
                          errors.customItems?.[index]?.key?.type ===
                            'required' && '入力してください'
                        }
                        error={!!errors.customItems?.[index]?.key}
                      />
                    </Cell>
                    <Cell sx={{width: '100%'}}>
                      <TextField
                        {...register(`customItems.${index}.value`, {
                          required: true,
                        })}
                        placeholder="項目内容"
                        size="small"
                        multiline
                        fullWidth
                        helperText={
                          errors.customItems?.[index]?.value &&
                          '入力してください'
                        }
                        error={!!errors.customItems?.[index]?.value}
                      />
                      <IconButton tooltip="削除" onClick={() => remove(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Cell>
                  </Stack>
                ))}
                <Box sx={{margin: '16px 0 24px'}}>
                  <AddItemButton onAdd={() => append({key: '', value: ''})}>
                    項目を追加
                  </AddItemButton>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
        <Stack spacing={1}>
          <StyledTypography>部屋情報</StyledTypography>
          <Stack padding="0 !important" width="100%" spacing={0}>
            <Typography sx={{fontWeight: 500, mb: 1}}>基本情報</Typography>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>号室</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  {...register('roomName', {
                    required: true,
                    maxLength: MAX_INPUT_LENGTH,
                  })}
                  helperText={
                    errors.roomName?.type === 'required' &&
                    '号室を入力してください'
                  }
                  error={errors.roomName?.type === 'required'}
                  size="small"
                />
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>間取り</LabelTitle>
              </Cell>
              <StyledCell>
                <FormControl fullWidth>
                  <Select
                    defaultValue={data.layout || ''}
                    displayEmpty
                    fullWidth
                    {...register('layout', {
                      required: true,
                    })}
                    error={errors.layout?.type === 'required'}>
                    <MenuItem value="">選択してください</MenuItem>
                    {layoutList.map((s, index) => (
                      <MenuItem value={s.value} key={index}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.layout?.type === 'required' && (
                    <FormHelperText sx={{color: 'warning.main'}}>
                      間取りを選択してください
                    </FormHelperText>
                  )}
                </FormControl>
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>部屋の状況</LabelTitle>
              </Cell>
              <StyledCell>
                <FormControl fullWidth>
                  <Select
                    defaultValue={
                      data.condition
                        ? conditionList.find(
                            list => list.value === data.condition,
                          )?.value
                        : ''
                    }
                    displayEmpty
                    {...register('condition', {
                      required: true,
                    })}
                    error={errors.condition?.type === 'required'}>
                    <MenuItem value="">選択してください</MenuItem>
                    {conditionList.map((s, index) => (
                      <MenuItem value={s.value} key={index}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.condition?.type === 'required' && (
                    <FormHelperText sx={{color: 'warning.main'}}>
                      部屋の状況を選択してください
                    </FormHelperText>
                  )}
                </FormControl>
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>引渡し日・決算日</LabelTitle>
              </Cell>
              <StyledCell>
                <DatePicker
                  views={['year', 'month', 'day']}
                  inputFormat={'yyyy/MM/dd'}
                  value={deliveryDate}
                  error={isNotValidDeliveryDate}
                  onChangeValue={setDeliveryDate}
                />
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>専有面積</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  {...register('occupiedArea', {
                    required: true,
                    maxLength: MAX_INPUT_LENGTH,
                  })}
                  helperText={
                    errors.occupiedArea?.type === 'required' &&
                    '専有面積を入力してください'
                  }
                  error={errors.occupiedArea?.type === 'required'}
                  size="small"
                />
                <Typography sx={{marginLeft: '4px', flexShrink: 0}}>
                  m²
                </Typography>
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>土地面積合計</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  {...register('totalArea', {
                    maxLength: MAX_INPUT_LENGTH,
                  })}
                  size="small"
                />
                <Typography sx={{marginLeft: '4px', flexShrink: 0}}>
                  m²
                </Typography>
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>購入会社名</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  {...register('salesCompany', {
                    maxLength: MAX_INPUT_LENGTH,
                  })}
                  size="small"
                />
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>銀行名（ローン会社名）</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  {...register('bankName', {
                    maxLength: MAX_INPUT_LENGTH,
                  })}
                  size="small"
                />
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>融資年数</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  type="number"
                  {...register('loanYears', {
                    required: true,
                    maxLength: MAX_INPUT_LENGTH,
                    max: 50,
                  })}
                  helperText={
                    (errors.loanYears?.type === 'required' &&
                      '融資年数を入力してください') ||
                    (errors.loanYears?.type === 'max' &&
                      '融資年数は最大50年までです')
                  }
                  error={
                    errors.loanYears?.type === 'required' ||
                    errors.loanYears?.type === 'max'
                  }
                  size="small"
                />
                <Typography sx={{marginLeft: '4px'}}>年</Typography>
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>融資金額</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  type="number"
                  {...register('loanAmount', {
                    required: true,
                    maxLength: MAX_INPUT_LENGTH,
                  })}
                  helperText={
                    errors.loanAmount?.type === 'required' &&
                    '融資金額を入力してください'
                  }
                  error={errors.loanAmount?.type === 'required'}
                  size="small"
                />
                <Typography sx={{marginLeft: '4px'}}>円</Typography>
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>金利</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  type="number"
                  {...register('interestRate', {
                    maxLength: MAX_INPUT_LENGTH,
                  })}
                  size="small"
                />
                <Typography sx={{marginLeft: '4px'}}>％</Typography>
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>返済方法</LabelTitle>
              </Cell>
              <StyledCell>
                <FormControl fullWidth>
                  <Select
                    defaultValue={data.repaymentMethod || ''}
                    displayEmpty
                    {...register('repaymentMethod')}>
                    <MenuItem value="">選択する</MenuItem>
                    {repaymentMethodList.map((s, index) => (
                      <MenuItem value={s.value} key={index}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>返済開始日</LabelTitle>
              </Cell>
              <StyledCell>
                <DatePicker
                  views={['year', 'month', 'day']}
                  inputFormat={'yyyy/MM/dd'}
                  value={repaymentDate}
                  onChangeValue={setRepaymentDate}
                />
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>売買価格</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  type="number"
                  {...register('sellingPrice', {
                    required: true,
                    maxLength: MAX_INPUT_LENGTH,
                  })}
                  helperText={
                    errors.sellingPrice?.type === 'required' &&
                    '売買価格を入力してください'
                  }
                  error={errors.sellingPrice?.type === 'required'}
                  size="small"
                />
                <Typography sx={{marginLeft: '4px'}}>円</Typography>
              </StyledCell>
            </Stack>
            <Stack direction="column" sx={{width: '100%'}}>
              {roomCustomItemsFields.map((field, index) => (
                <Stack key={field.id} direction="row" spacing={0}>
                  <Cell color="primary-40">
                    <TextField
                      {...register(`roomCustomItems.${index}.key`, {
                        required: true,
                        maxLength: MAX_INPUT_LENGTH,
                      })}
                      placeholder="項目名"
                      size="small"
                      helperText={
                        errors.roomCustomItems?.[index]?.key?.type ===
                          'required' && '入力してください'
                      }
                      error={!!errors.roomCustomItems?.[index]?.key}
                    />
                  </Cell>
                  <Cell sx={{width: '100%'}}>
                    <TextField
                      {...register(`roomCustomItems.${index}.value`, {
                        required: true,
                      })}
                      placeholder="項目内容"
                      size="small"
                      multiline
                      fullWidth
                      helperText={
                        errors.roomCustomItems?.[index]?.value &&
                        '入力してください'
                      }
                      error={!!errors.roomCustomItems?.[index]?.value}
                    />
                    <IconButton
                      tooltip="削除"
                      onClick={() => removeRoomCustomItems(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Cell>
                </Stack>
              ))}
              <Box sx={{margin: '16px 0 24px'}}>
                <Button
                  size="small"
                  startIcon={<PlusIcon strokeColor="#31CDD3" />}
                  onClick={() => appendRoomCustomItems({key: '', value: ''})}>
                  項目を追加
                </Button>
              </Box>
            </Stack>
            <RoomCostInfo
              costInfo={{
                purchaseCosts: data.purchaseCosts,
                propertyTax: data.propertyTax,
                realEstateAcquisitionTax: data.realEstateAcquisitionTax,
                insuranceFee: data.insuranceFee,
                repairReserveFund: data.repairReserveFund,
                managementFee: data.managementFee,
                managementCommission: data.managementCommission,
                customCostItems: data.customCostItems,
              }}
              onCostInfoChange={onChangeCostInfo}
              onErrorChange={setIsRoomInfoError}
              isSubmitted={isSaveButtonDown}
            />
            <RoomEarlyRepaymentInfo
              onEarlyRepaymentInfoChange={onChangeEarlyRepaymentInfo}
              onErrorChange={setIsRoomInfoError}
              isSubmitted={isSaveButtonDown}
            />
          </Stack>
        </Stack>
        <Stack spacing={1}>
          <StyledTypography>契約者情報</StyledTypography>
          <Grid item xs={12}>
            <Typography sx={{fontWeight: 500, mb: '8px'}}>基本情報</Typography>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle isRequired>入居開始日</LabelTitle>
              </Cell>
              <StyledCell>
                <DatePicker
                  inputFormat={'yyyy/MM/dd'}
                  value={startDate}
                  error={isNotValidStartDate}
                  onChangeValue={setStartDate}
                />
              </StyledCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>入居者氏名</LabelTitle>
              </Cell>
              <StyledCell>
                <TextField
                  {...register('residentName')}
                  size="small"
                  fullWidth
                />
              </StyledCell>
            </Stack>
            <Stack direction="column" sx={{width: '100%'}}>
              {customItemsFields.map((field, index) => (
                <Stack key={field.id} direction="row" spacing={0}>
                  <Cell color="primary-40">
                    <TextField
                      {...register(`customItems.${index}.key`, {
                        required: true,
                        maxLength: MAX_INPUT_LENGTH,
                      })}
                      placeholder="項目名"
                      size="small"
                      helperText={
                        errors.customItems?.[index]?.key?.type === 'required' &&
                        '入力してください'
                      }
                      error={!!errors.customItems?.[index]?.key}
                    />
                  </Cell>
                  <Cell sx={{width: '100%'}}>
                    <TextField
                      {...register(`customItems.${index}.value`, {
                        required: true,
                      })}
                      placeholder="項目内容"
                      size="small"
                      multiline
                      fullWidth
                      helperText={
                        errors.customItems?.[index]?.value && '入力してください'
                      }
                      error={!!errors.customItems?.[index]?.value}
                    />
                    <IconButton
                      tooltip="削除"
                      onClick={() => customItemsRemove(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Cell>
                </Stack>
              ))}
              <Box sx={{margin: '8px 0 24px'}}>
                <AddItemButton
                  onAdd={() => customItemsAppend({key: '', value: ''})}>
                  項目を追加
                </AddItemButton>
              </Box>
            </Stack>
            <Typography sx={{fontWeight: 500, mb: '8px'}}>収入</Typography>
            <MoneyInfoTableHead />
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>礼金</LabelTitle>
              </Cell>
              <StyledRepeatRuleCell>
                <Stack spacing={1} mb="8px">
                  {keyMoneyFields.map((field, index) => (
                    <MoneyInfo
                      key={field.id}
                      register={register}
                      name="keyMoney"
                      index={index}
                      defaultValue={field}
                      errors={
                        Array.isArray(errors.keyMoney)
                          ? errors.keyMoney
                          : undefined
                      }
                      onRemove={keyMoneyRemove}
                    />
                  ))}
                  <AddItemButton
                    onAdd={() =>
                      keyMoneyAppend({...initialValue, frequency: 'once'})
                    }>
                    繰り返しルールを追加
                  </AddItemButton>
                </Stack>
              </StyledRepeatRuleCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>初月賃料</LabelTitle>
              </Cell>
              <StyledRepeatRuleCell>
                <Stack spacing={1} mb="8px">
                  {firstIncomeFields.map((field, index) => (
                    <MoneyInfo
                      key={field.id}
                      register={register}
                      name="firstIncome"
                      index={index}
                      defaultValue={field}
                      errors={
                        Array.isArray(errors.firstIncome)
                          ? errors.firstIncome
                          : undefined
                      }
                      onRemove={firstIncomeRemove}
                    />
                  ))}
                  <AddItemButton
                    onAdd={() =>
                      firstIncomeAppend({...initialValue, frequency: 'once'})
                    }>
                    繰り返しルールを追加
                  </AddItemButton>
                </Stack>
              </StyledRepeatRuleCell>
            </Stack>
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>月額賃料</LabelTitle>
              </Cell>
              <StyledRepeatRuleCell>
                <Stack spacing={1} mb="8px">
                  {rentFields.map((field, index) => (
                    <MoneyInfo
                      key={field.id}
                      register={register}
                      name="rent"
                      index={index}
                      defaultValue={field}
                      errors={
                        Array.isArray(errors.rent) ? errors.rent : undefined
                      }
                      onRemove={rentRemove}
                    />
                  ))}
                  <AddItemButton onAdd={() => rentAppend({...initialValue})}>
                    繰り返しルールを追加
                  </AddItemButton>
                </Stack>
              </StyledRepeatRuleCell>
            </Stack>
            {customMoveInCostItemsFields.map((field, index) => (
              <Grid item xs={12} key={index}>
                <Stack key={field.id} direction="row" spacing={0}>
                  <Cell color="primary-40">
                    <TextField
                      {...register(`customMoveInCostItems.${index}.key`, {
                        required: true,
                        maxLength: MAX_INPUT_LENGTH,
                      })}
                      size="small"
                      placeholder="項目名"
                      helperText={
                        Array.isArray(errors.customMoveInCostItems) &&
                        !!errors.customMoveInCostItems[index] &&
                        Object.keys(errors.customMoveInCostItems[index]).some(
                          key => key === 'key',
                        ) &&
                        errors.customMoveInCostItems[index].key?.type ===
                          'required' &&
                        '入力してください'
                      }
                      error={
                        Array.isArray(errors.customMoveInCostItems) &&
                        !!errors.customMoveInCostItems[index] &&
                        Object.keys(errors.customMoveInCostItems[index]).some(
                          key => key === 'key',
                        ) &&
                        errors.customMoveInCostItems[index].key?.type ===
                          'required'
                      }
                    />
                  </Cell>
                  <StyledRepeatRuleCell sx={{justifyContent: 'space-between'}}>
                    <Stack spacing={1} mb="8px">
                      {field.value &&
                        field.value.map((value, i) => (
                          <Stack
                            key={`${field.id}-${i}`}
                            direction="row"
                            spacing={3}
                            alignItems="center">
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}>
                              <TextField
                                {...register(
                                  `customMoveInCostItems.${index}.value.${i}.money`,
                                  {
                                    required: true,
                                  },
                                )}
                                type="number"
                                sx={{width: '180px'}}
                                size="small"
                                // TODO: https://geekbox.atlassian.net/browse/FLIK-116
                                helperText={
                                  Array.isArray(errors.customMoveInCostItems) &&
                                  !!errors.customMoveInCostItems[index] &&
                                  Object.keys(
                                    errors.customMoveInCostItems[index],
                                  ).some(key => key === 'value') &&
                                  '入力してください'
                                }
                                error={
                                  Array.isArray(errors.customMoveInCostItems) &&
                                  !!errors.customMoveInCostItems[index] &&
                                  Object.keys(
                                    errors.customMoveInCostItems[index],
                                  ).some(key => key === 'value')
                                }
                              />
                              <Typography>円</Typography>
                            </Stack>
                            <Select
                              {...register(
                                `customMoveInCostItems.${index}.value.${i}.frequency`,
                              )}
                              defaultValue={value?.frequency}
                              sx={{width: '130px'}}>
                              {frequencyList.map((s, index) => (
                                <MenuItem value={s.value} key={index}>
                                  {s.name}
                                </MenuItem>
                              ))}
                            </Select>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}>
                                <Select
                                  {...register(
                                    `customMoveInCostItems.${index}.value.${i}.year`,
                                  )}
                                  defaultValue={value?.year}
                                  sx={{width: '100px'}}>
                                  {yearsList.map((s, index) => (
                                    <MenuItem value={s.value} key={index}>
                                      {s.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography>年</Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}>
                                <Select
                                  {...register(
                                    `customMoveInCostItems.${index}.value.${i}.month`,
                                  )}
                                  defaultValue={value?.month}
                                  sx={{width: '100px'}}>
                                  {monthList.map((s, index) => (
                                    <MenuItem value={s.value} key={index}>
                                      {s.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography>月</Typography>
                              </Stack>
                            </Stack>
                            {i !== 0 && (
                              <DeleteRuleButton
                                onClick={() => {
                                  const items =
                                    getValues('customMoveInCostItems') ?? [];
                                  items[index]?.value?.splice(i, 1);
                                  customMoveInCostItemsUpdate(
                                    index,
                                    items[index],
                                  );
                                }}
                              />
                            )}
                          </Stack>
                        ))}
                      <AddItemButton
                        onAdd={() => {
                          const items =
                            getValues('customMoveInCostItems') ?? [];
                          items[index]?.value?.push({
                            ...initialValue,
                          });
                          customMoveInCostItemsUpdate(index, items[index]);
                        }}>
                        繰り返しルールを追加
                      </AddItemButton>
                    </Stack>
                    <IconButton
                      tooltip="削除"
                      onClick={() => customMoveInCostItemsRemove(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </StyledRepeatRuleCell>
                </Stack>
              </Grid>
            ))}
            <Grid item>
              <Box sx={{margin: '8px 0 24px'}}>
                <AddItemButton
                  onAdd={() =>
                    customMoveInCostItemsAppend({
                      key: '',
                      value: [{...initialValue}],
                    })
                  }>
                  収入を追加
                </AddItemButton>
              </Box>
            </Grid>
            <Typography sx={{fontWeight: 500, mb: '8px'}}>費用</Typography>
            <MoneyInfoTableHead />
            <Stack direction="row" spacing={0}>
              <Cell color="primary-40">
                <LabelTitle>広告料</LabelTitle>
              </Cell>
              <StyledRepeatRuleCell>
                <Stack spacing={1} mb="8px">
                  {advertisingRatesFields.map((field, index) => (
                    <MoneyInfo
                      key={field.id}
                      register={register}
                      name="advertisingRates"
                      index={index}
                      defaultValue={field}
                      errors={
                        Array.isArray(errors.advertisingRates)
                          ? errors.advertisingRates
                          : undefined
                      }
                      onRemove={advertisingRatesRemove}
                    />
                  ))}
                  <AddItemButton
                    onAdd={() =>
                      advertisingRatesAppend({
                        ...initialValue,
                        frequency: 'once',
                      })
                    }>
                    繰り返しルールを追加
                  </AddItemButton>
                </Stack>
              </StyledRepeatRuleCell>
            </Stack>
            {customMoveOutCostItemsFields.map((field, index) => (
              <Grid item xs={12} key={index}>
                <Stack key={field.id} direction="row" spacing={0}>
                  <Cell color="primary-40">
                    <TextField
                      {...register(`customMoveOutCostItems.${index}.key`, {
                        required: true,
                        maxLength: MAX_INPUT_LENGTH,
                      })}
                      size="small"
                      placeholder="項目名"
                      helperText={
                        Array.isArray(errors.customMoveOutCostItems) &&
                        !!errors.customMoveOutCostItems[index] &&
                        Object.keys(errors.customMoveOutCostItems[index]).some(
                          key => key === 'key',
                        ) &&
                        errors.customMoveOutCostItems[index].key?.type ===
                          'required' &&
                        '入力してください'
                      }
                      error={
                        Array.isArray(errors.customMoveOutCostItems) &&
                        !!errors.customMoveOutCostItems[index] &&
                        Object.keys(errors.customMoveOutCostItems[index]).some(
                          key => key === 'key',
                        ) &&
                        errors.customMoveOutCostItems[index].key?.type ===
                          'required'
                      }
                    />
                  </Cell>
                  <StyledRepeatRuleCell sx={{justifyContent: 'space-between'}}>
                    <Stack spacing={1} mb="8px">
                      {field.value &&
                        field.value.map((value, i) => (
                          <Stack
                            key={`${field.id}-${i}`}
                            direction="row"
                            spacing={3}
                            alignItems="center">
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}>
                              <TextField
                                {...register(
                                  `customMoveOutCostItems.${index}.value.${i}.money`,
                                  {
                                    required: true,
                                  },
                                )}
                                type="number"
                                sx={{width: '180px'}}
                                size="small"
                                // TODO: https://geekbox.atlassian.net/browse/FLIK-116
                                helperText={
                                  Array.isArray(
                                    errors.customMoveOutCostItems,
                                  ) &&
                                  !!errors.customMoveOutCostItems[index] &&
                                  Object.keys(
                                    errors.customMoveOutCostItems[index],
                                  ).some(key => key === 'value') &&
                                  '入力してください'
                                }
                                error={
                                  Array.isArray(
                                    errors.customMoveOutCostItems,
                                  ) &&
                                  !!errors.customMoveOutCostItems[index] &&
                                  Object.keys(
                                    errors.customMoveOutCostItems[index],
                                  ).some(key => key === 'value')
                                }
                              />
                              <Typography>円</Typography>
                            </Stack>
                            <Select
                              {...register(
                                `customMoveOutCostItems.${index}.value.${i}.frequency`,
                              )}
                              defaultValue={value?.frequency}
                              sx={{width: '130px'}}>
                              {frequencyList.map((s, index) => (
                                <MenuItem value={s.value} key={index}>
                                  {s.name}
                                </MenuItem>
                              ))}
                            </Select>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}>
                                <Select
                                  {...register(
                                    `customMoveOutCostItems.${index}.value.${i}.year`,
                                  )}
                                  defaultValue={value?.year}
                                  sx={{width: '100px'}}>
                                  {yearsList.map((s, index) => (
                                    <MenuItem value={s.value} key={index}>
                                      {s.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography>年</Typography>
                              </Stack>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}>
                                <Select
                                  {...register(
                                    `customMoveOutCostItems.${index}.value.${i}.month`,
                                  )}
                                  defaultValue={value?.month}
                                  sx={{width: '100px'}}>
                                  {monthList.map((s, index) => (
                                    <MenuItem value={s.value} key={index}>
                                      {s.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography>月</Typography>
                              </Stack>
                            </Stack>
                            {i !== 0 && (
                              <DeleteRuleButton
                                onClick={() => {
                                  const items =
                                    getValues('customMoveOutCostItems') ?? [];
                                  items[index]?.value?.splice(i, 1);
                                  customMoveOutCostItemsUpdate(
                                    index,
                                    items[index],
                                  );
                                }}
                              />
                            )}
                          </Stack>
                        ))}
                      <AddItemButton
                        onAdd={() => {
                          const items =
                            getValues('customMoveOutCostItems') ?? [];
                          items[index]?.value?.push({
                            ...initialValue,
                          });
                          customMoveOutCostItemsUpdate(index, items[index]);
                        }}>
                        繰り返しルールを追加
                      </AddItemButton>
                    </Stack>
                    <IconButton
                      tooltip="削除"
                      onClick={() => customMoveOutCostItemsRemove(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </StyledRepeatRuleCell>
                </Stack>
              </Grid>
            ))}
            <Grid item>
              <Box sx={{margin: '8px 0 24px'}}>
                <AddItemButton
                  onAdd={() =>
                    customMoveOutCostItemsAppend({
                      key: '',
                      value: [{...initialValue}],
                    })
                  }>
                  費用を追加
                </AddItemButton>
              </Box>
            </Grid>
          </Grid>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="end">
          <Button variant="outlined" onClick={() => navigate('/')}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            disabled={isLoading}
            startIcon={
              isLoading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '20px  !important',
                    height: '20px  !important',
                  }}
                />
              ) : null
            }
            onMouseDown={() => setIsSaveButtonDown(true)}
            onClick={handleSubmit(onSubmit, onError)}>
            {isLoading ? '保存中' : '保存する'}
          </Button>
        </Stack>
      </Stack>
      <Snackbar
        severity="error"
        message="入力内容を確認してください"
        open={openErrorSnackbar}
        onClose={() => setOpenErrorSnackbar(false)}
      />
    </>
  );
};

const StyledTypography = styled(Typography)({
  fontWeight: '600',
  fontSize: '18px',
  color: 'white',
  padding: '8px 12px',
  backgroundColor: '#31CDD3',
  borderRadius: '6px',
});

const StyledCell = styled(Cell)({
  width: '100%',
});

const StyledText = styled(Typography)({
  fontSize: '12px',
  lineHeight: 1.3,
  color: '#DA0000',
  width: '320px',
});

const StyledImg = styled('img')`
  border-radius: 12px;
  width: auto;
  height: 100px;
  object-fit: cover;
`;

const StyledRepeatRuleCell = styled(Cell)({
  width: '100%',
});
